import { useEffect, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  type TableColumnsType,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { ListenerName, listenerText } from '../../lib/listenerName';
import { Can } from '../../rbac/RequirePermission';
import { usePermissions } from '../../auth/store';
import {
  isValidLanguageCode,
  isValidSentence,
  predictionTone,
  type PredictedGender,
  type VoiceStatus,
} from './voiceVerification';

interface VoiceRow {
  id: string;
  status: VoiceStatus;
  listener: { id: string; displayName: string; handle: string };
  declaredGender: string;
  predictedGender: PredictedGender;
  confidence: number;
  medianPitchHz: number | null;
  analysisMethod: string;
  sentenceText: string;
  languageCode: string;
  durationMs: number;
  autoDecided: boolean;
  rejectionReason: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

interface VoiceSentence {
  id: string;
  languageCode: string;
  text: string;
  isActive: boolean;
  sortOrder: number;
}

interface VoiceSettings {
  required: boolean;
  autoApprove: boolean;
  minConfidencePct: number;
}

/**
 * Listener voice verification: review recordings, manage the per-language
 * sentences listeners read aloud, and the go-online gate. See
 * maja_api/docs/voice-verification.md.
 */
export function VoiceVerificationPage() {
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
        Voice verification
      </Typography.Title>
      <Card>
        <Tabs
          items={[
            { key: 'queue', label: 'Review queue', children: <QueueTab /> },
            { key: 'sentences', label: 'Sentences', children: <SentencesTab /> },
            { key: 'settings', label: 'Settings', children: <SettingsTab /> },
          ]}
        />
      </Card>
    </Space>
  );
}

// ============================================================
// Review queue
// ============================================================

function QueueTab() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [status, setStatus] = useState<VoiceStatus | 'all'>('pending_review');
  // Recordings as local blob: URLs, fetched one row at a time on "Listen" —
  // every signed-URL fetch is audited, so never prefetch the whole table.
  const [audio, setAudio] = useState<Record<string, string>>({});
  // Free the decoded recordings when the page goes away.
  useEffect(
    () => () => {
      setAudio((a) => {
        Object.values(a).forEach((u) => URL.revokeObjectURL(u));
        return {};
      });
    },
    [],
  );
  const [rejecting, setRejecting] = useState<VoiceRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const queueKey = ['admin', 'voice', 'queue', status];
  const { data, isLoading } = useQuery({
    queryKey: queueKey,
    queryFn: async () =>
      (
        await api.get<{ items: VoiceRow[]; nextCursor: string | null }>('/admin/voice-verification/queue', {
          params: { status, limit: 100 },
        })
      ).data,
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['admin', 'voice', 'queue'] });

  const verify = useMutation({
    mutationFn: (id: string) => api.post(`/admin/voice-verification/${id}/verify`),
    onSuccess: () => {
      message.success('Approved');
      invalidate();
    },
    onError: () => message.error('Could not approve'),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/admin/voice-verification/${id}/reject`, { reason }),
    onSuccess: () => {
      message.success('Rejected');
      invalidate();
      setRejecting(null);
      setRejectReason('');
    },
    onError: () => message.error('Could not reject'),
  });

  async function listen(row: VoiceRow) {
    try {
      const res = await api.get<{ url: string }>(`/admin/voice-verification/${row.id}/audio-url`);
      // Fetch the bytes (CORS) rather than pointing <audio> at the signed URL:
      // the API sends `Cross-Origin-Resource-Policy: same-origin`, so a media
      // element on the admin's origin is refused the cross-origin load.
      const blob = await fetch(res.data.url, { credentials: 'omit' }).then((r) => {
        if (!r.ok) throw new Error(`recording fetch ${r.status}`);
        return r.blob();
      });
      setAudio((a) => ({ ...a, [row.id]: URL.createObjectURL(blob) }));
    } catch {
      message.error('Could not load the recording');
    }
  }

  const columns: TableColumnsType<VoiceRow> = [
    {
      title: 'Listener',
      render: (_: unknown, row) => (
        <ListenerName listener={row.listener} listenerId={row.listener.id} tab="application" />
      ),
    },
    { title: 'Declared', dataIndex: 'declaredGender' },
    {
      title: 'On-device (advisory)',
      render: (_: unknown, row) => (
        <Space direction="vertical" size={2}>
          <Tag color={predictionTone(row.declaredGender, row.predictedGender)}>
            {row.predictedGender} · {Math.round(row.confidence * 100)}%
          </Tag>
          <span style={{ fontSize: 11, color: '#8B8B8B' }}>
            {row.medianPitchHz ? `${Math.round(row.medianPitchHz)} Hz` : '—'} · {row.analysisMethod}
          </span>
        </Space>
      ),
    },
    {
      title: 'Sentence',
      render: (_: unknown, row) => (
        <Space direction="vertical" size={2} style={{ maxWidth: 280 }}>
          <span>
            <Tag>{row.languageCode}</Tag>
            {row.sentenceText}
          </span>
          <span style={{ fontSize: 11, color: '#8B8B8B' }}>{(row.durationMs / 1000).toFixed(1)} s</span>
        </Space>
      ),
    },
    {
      title: 'Status',
      render: (_: unknown, row) => (
        <Space direction="vertical" size={2}>
          <Tag color={row.status === 'verified' ? 'success' : row.status === 'rejected' ? 'error' : 'processing'}>
            {row.status}
          </Tag>
          {row.autoDecided && <Tag color="purple">auto-approved</Tag>}
          {row.rejectionReason && (
            <span style={{ fontSize: 11, color: '#8B8B8B' }}>{row.rejectionReason}</span>
          )}
        </Space>
      ),
    },
    {
      title: 'Submitted',
      dataIndex: 'createdAt',
      render: (s: string) => new Date(s).toLocaleString(),
    },
    {
      title: 'Actions',
      render: (_: unknown, row) => (
        <Space direction="vertical">
          <Can permission="voice.recording.listen">
            {audio[row.id] ? (
              <audio controls src={audio[row.id]} style={{ height: 32 }} />
            ) : (
              <Button size="small" onClick={() => void listen(row)}>
                Listen
              </Button>
            )}
          </Can>
          <Space>
            <Can permission="voice.decide">
              {row.status !== 'verified' && (
                <Button
                  size="small"
                  type="primary"
                  loading={verify.isPending && verify.variables === row.id}
                  onClick={() => verify.mutate(row.id)}
                >
                  Approve
                </Button>
              )}
            </Can>
            <Can permission="voice.decide">
              {row.status !== 'rejected' && (
                <Button size="small" danger onClick={() => setRejecting(row)}>
                  {row.status === 'verified' ? 'Revoke' : 'Reject'}
                </Button>
              )}
            </Can>
          </Space>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Select
        style={{ width: 200 }}
        value={status}
        onChange={setStatus}
        options={[
          { value: 'pending_review', label: 'Pending review' },
          { value: 'verified', label: 'Verified' },
          { value: 'rejected', label: 'Rejected' },
          { value: 'all', label: 'All' },
        ]}
      />
      <Table
        rowKey="id"
        loading={isLoading}
        dataSource={data?.items ?? []}
        columns={columns}
        pagination={{ pageSize: 25 }}
      />

      <Modal
        title={rejecting ? `${rejecting.status === 'verified' ? 'Revoke' : 'Reject'} voice for ${listenerText(rejecting.listener)}` : ''}
        open={!!rejecting}
        onCancel={() => setRejecting(null)}
        onOk={() => rejecting && reject.mutate({ id: rejecting.id, reason: rejectReason })}
        okButtonProps={{
          danger: true,
          disabled: rejectReason.trim().length < 10 || rejectReason.length > 500,
          loading: reject.isPending,
        }}
        okText={rejecting?.status === 'verified' ? 'Revoke' : 'Reject'}
      >
        {rejecting?.status === 'verified' && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="This revokes an approved verification. While the gate is on, the listener cannot go online until they verify again."
          />
        )}
        <Typography.Paragraph type="secondary">
          The reason will be shown to the listener. Be specific (10–500 chars).
        </Typography.Paragraph>
        <Input.TextArea
          rows={4}
          maxLength={500}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="e.g. The recording is silent; please record again in a quiet place."
        />
      </Modal>
    </Space>
  );
}

// ============================================================
// Sentences
// ============================================================

const emptySentence = { languageCode: '', text: '', sortOrder: 0 };

function SentencesTab() {
  const canManage = usePermissions().can('voice.sentences.manage');
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [language, setLanguage] = useState<string | undefined>();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptySentence);
  const [editing, setEditing] = useState<VoiceSentence | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'voice', 'sentences'],
    queryFn: async () => (await api.get<VoiceSentence[]>('/admin/voice-verification/sentences')).data,
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['admin', 'voice', 'sentences'] });

  const create = useMutation({
    mutationFn: () => api.post('/admin/voice-verification/sentences', { ...form, text: form.text.trim() }),
    onSuccess: () => {
      message.success('Sentence added');
      setCreateOpen(false);
      setForm(emptySentence);
      invalidate();
    },
    onError: () => message.error('Could not add sentence'),
  });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<VoiceSentence> }) =>
      api.patch(`/admin/voice-verification/sentences/${id}`, body),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
    onError: () => message.error('Could not save sentence'),
  });

  const languages = [...new Set((data ?? []).map((s) => s.languageCode))].sort();
  const rows = (data ?? []).filter((s) => !language || s.languageCode === language);

  const columns: TableColumnsType<VoiceSentence> = [
    { title: 'Language', dataIndex: 'languageCode', width: 100, render: (c: string) => <Tag>{c}</Tag> },
    { title: 'Sentence', dataIndex: 'text' },
    { title: 'Order', dataIndex: 'sortOrder', width: 80 },
    {
      title: 'Active',
      width: 90,
      render: (_: unknown, row) => (
        <Switch
          checked={row.isActive}
          loading={patch.isPending && patch.variables?.id === row.id}
          disabled={patch.isPending || !canManage}
          onChange={(v) => patch.mutate({ id: row.id, body: { isActive: v } })}
        />
      ),
    },
    {
      title: '',
      width: 80,
      render: (_: unknown, row) => (
        <Can permission="voice.sentences.manage">
          <Button size="small" onClick={() => setEditing(row)}>
            Edit
          </Button>
        </Can>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="Listeners see a random active sentence in their language. A language with no active sentence falls back to English."
      />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Select
          allowClear
          placeholder="All languages"
          style={{ width: 200 }}
          value={language}
          onChange={setLanguage}
          options={languages.map((c) => ({ value: c, label: c }))}
        />
        <Can permission="voice.sentences.manage">
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            New sentence
          </Button>
        </Can>
      </div>
      <Table rowKey="id" loading={isLoading} dataSource={rows} columns={columns} pagination={false} />

      <Modal
        title="New sentence"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => create.mutate()}
        okButtonProps={{
          disabled: !isValidLanguageCode(form.languageCode) || !isValidSentence(form.text),
          loading: create.isPending,
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Input
            placeholder="ISO 639 code · e.g. hi, mr, brx"
            value={form.languageCode}
            onChange={(e) => setForm({ ...form, languageCode: e.target.value.toLowerCase().slice(0, 8) })}
          />
          <Input.TextArea
            rows={3}
            maxLength={300}
            placeholder="A short, natural sentence in that language (10–300 chars)"
            value={form.text}
            onChange={(e) => setForm({ ...form, text: e.target.value })}
          />
          <InputNumber
            min={0}
            addonBefore="Order"
            value={form.sortOrder}
            onChange={(v) => setForm({ ...form, sortOrder: v ?? 0 })}
          />
        </Space>
      </Modal>

      <Modal
        title={editing ? `Edit ${editing.languageCode} sentence` : ''}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={() =>
          editing &&
          patch.mutate({ id: editing.id, body: { text: editing.text.trim(), sortOrder: editing.sortOrder } })
        }
        okButtonProps={{ disabled: !editing || !isValidSentence(editing.text), loading: patch.isPending }}
      >
        {editing && (
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <Input.TextArea
              rows={3}
              maxLength={300}
              value={editing.text}
              onChange={(e) => setEditing({ ...editing, text: e.target.value })}
            />
            <InputNumber
              min={0}
              addonBefore="Order"
              value={editing.sortOrder}
              onChange={(v) => setEditing({ ...editing, sortOrder: v ?? 0 })}
            />
          </Space>
        )}
      </Modal>
    </Space>
  );
}

// ============================================================
// Settings
// ============================================================

function SettingsTab() {
  const canManage = usePermissions().can('voice.settings.manage');
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [draft, setDraft] = useState<VoiceSettings | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'voice', 'settings'],
    queryFn: async () => (await api.get<VoiceSettings>('/admin/voice-verification/settings')).data,
  });

  useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  const save = useMutation({
    mutationFn: (body: VoiceSettings) => api.post<VoiceSettings>('/admin/voice-verification/settings', body),
    onSuccess: (res) => {
      message.success('Settings saved');
      qc.setQueryData(['admin', 'voice', 'settings'], res.data);
    },
    onError: () => message.error('Could not save settings'),
  });

  if (isLoading || !draft) return <Card loading />;

  const turningGateOn = draft.required && !data?.required;
  const dirty = JSON.stringify(draft) !== JSON.stringify(data);

  return (
    <Space direction="vertical" size={20} style={{ width: '100%', maxWidth: 640 }}>
      <Space direction="vertical" size={6}>
        <Space>
          <Switch
            checked={draft.required}
            disabled={!canManage}
            onChange={(v) => setDraft({ ...draft, required: v })}
          />
          <span style={{ fontWeight: 600 }}>Require voice verification to go online</span>
        </Space>
        {turningGateOn && (
          <Alert
            type="warning"
            showIcon
            message="Unverified listeners who are online now will be taken offline at their next heartbeat (within ~20 s), and cannot go online until verified."
          />
        )}
      </Space>

      <Space direction="vertical" size={6}>
        <Space>
          <Switch
            checked={draft.autoApprove}
            disabled={!canManage}
            onChange={(v) => setDraft({ ...draft, autoApprove: v })}
          />
          <span style={{ fontWeight: 600 }}>Auto-approve when the on-device result matches</span>
        </Space>
        <Alert
          type="info"
          showIcon
          message="The on-device result can be forged by a modified app. Recordings are kept either way, and an auto-approval can be revoked from the queue."
        />
      </Space>

      <InputNumber
        min={50}
        max={100}
        addonBefore="Min confidence"
        addonAfter="%"
        disabled={!canManage || !draft.autoApprove}
        value={draft.minConfidencePct}
        onChange={(v) => setDraft({ ...draft, minConfidencePct: v ?? 85 })}
      />

      <Can permission="voice.settings.manage">
        {turningGateOn ? (
          <Popconfirm
            title="Turn on the go-online gate?"
            description="Unverified listeners will be taken offline."
            onConfirm={() => save.mutate(draft)}
          >
            <Button type="primary" loading={save.isPending} disabled={!dirty}>
              Save
            </Button>
          </Popconfirm>
        ) : (
          <Button type="primary" loading={save.isPending} disabled={!dirty} onClick={() => save.mutate(draft)}>
            Save
          </Button>
        )}
      </Can>
    </Space>
  );
}
